import { useEffect } from "react";
import { createRootRoute } from "@tanstack/react-router";
import { DecoRootLayout } from "@decocms/tanstack";
import { installNavTrackingHook, scheduleNavTrackingFlush, flushBuffer } from "~/sdk/navTracking";
import { syncMetaUserData } from "~/sdk/metaUserData";
import { useAttributionSync } from "~/hooks/useAttributionSync";
import { getOrCreateVisitorId } from "~/utils/visitorId";
import CookieConsent from "~/components/ui/CookieConsent";
// @ts-ignore Vite ?url import
import appCss from "../styles/app.css?url";

// CMS `Site.seo` overrides these per-page once block resolution kicks in.
const DEFAULT_DESCRIPTION =
  "Pijamas Femininos, Masculinos e Infantis, Lingerie, Biquíni e Fitness na Loja Daniela Tombini. Vendas por Atacado e Varejo.";

export const Route = createRootRoute({
  head: () => ({
    meta: [
      { charSet: "utf-8" },
      { name: "viewport", content: "width=device-width, initial-scale=1" },
      { title: "Daniela Tombini: Pijamas Femininos, Lingerie, Biquíni, Fitness, Praia" },
      { name: "description", content: DEFAULT_DESCRIPTION },
      { property: "og:site_name", content: "Daniela Tombini" },
      { property: "og:locale", content: "pt_BR" },
    ],
    links: [
      { rel: "preconnect", href: "https://decoims.com", crossOrigin: "anonymous" as const },
      { rel: "preconnect", href: "https://danielatombini.vtexassets.com", crossOrigin: "anonymous" as const },
      { rel: "preconnect", href: "https://fonts.googleapis.com" },
      { rel: "preconnect", href: "https://fonts.gstatic.com", crossOrigin: "anonymous" as const },
      {
        rel: "stylesheet",
        id: "gfonts-css",
        media: "print",
        href:
          "https://fonts.googleapis.com/css2?family=Poppins:wght@300;400;500;600;700;800&family=Montserrat:wght@400..700&family=Lato:wght@500&display=swap",
      },

      { rel: "stylesheet", href: appCss },
      { rel: "icon", href: "/favicon.ico" },
      { rel: "dns-prefetch", href: "https://sp.vtex.com" },
    ],
    scripts: [FONT_SWAP_SCRIPT, ...THIRD_PARTY_SCRIPTS],
  }),
  component: RootLayout,
});

/**
 * A folha de fontes do Google carrega com `media="print"` (não bloqueia o
 * render) — esse script troca pra `media="all"` assim que ela terminar de
 * baixar, via evento real de JS. O truque clássico de fazer essa troca só
 * com o atributo HTML `onload="..."` não funciona aqui: testado ao vivo,
 * esse atributo (string, não função) é descartado pelo pipeline de
 * serialização do `head()` do TanStack Start/React 19 — some do HTML
 * renderizado via SSR. Precisa ser um <script> de verdade que roda depois
 * do <link> já estar no DOM (confirmado: `scripts` sempre renderiza depois
 * de `links` na ordem real do documento).
 *
 * `l.sheet` é checado antes de anexar o listener: em visita repetida (CSS
 * já em cache do navegador), a folha pode terminar de carregar e disparar
 * `load` ANTES desse script rodar — o listener chegaria tarde demais e
 * nunca dispararia, travando o `<link>` em `media="print"` pra sempre.
 * `l.sheet` é non-null assim que a folha já carregou e foi parseada,
 * então cobre esse caso mesmo sem o evento.
 */
const FONT_SWAP_SCRIPT = {
  id: "gfonts-media-swap",
  children:
    `(function(){var l=document.getElementById("gfonts-css");if(!l)return;var a=function(){l.media="all";};if(l.sheet){a();}else{l.addEventListener("load",a);}})();`,
};

/**
 * Scripts de terceiros portados do v1, levantados via captura de rede/HTML
 * real do site em produção (PLANO-REDESIGN.md, item 20). Deliberadamente
 * NÃO inclui tudo que existia no v1:
 *
 * - Google+ (`apis.google.com/.../plusone.js`) — produto morto desde 2019.
 * - `s.lilstts.com/deco.js` ("stonks") e os scripts inline que ligam o
 *   barramento de eventos `window.DECO.events` ao `dataLayer` — eram
 *   internos da plataforma SaaS da deco.cx, que este projeto abandonou.
 * - `unpkg.com/htmx.org` — dependência do framework v1, carregada de forma
 *   bloqueante; o v2 (React/TanStack) não usa htmx.
 * - As chamadas `dito.track({action:"acessou-categoria",...})` — dependiam
 *   de `google_tag_manager["rm"][...]`, acessando variáveis internas de uma
 *   instância específica do GTM por índice; frágil demais pra replicar às
 *   cegas (o índice muda a cada nova publicação do container no GTM).
 * - **Dito inteiro** — eliminado em 08/07/2026 (decisão do André: substituir
 *   por CRM próprio, ver PLANO-REDESIGN.md item 23).
 * - **CRMBack inteiro** (recuperação de carrinho) — eliminado em 08/07/2026,
 *   mesma decisão: recuperação de carrinho passa a ser feita pelo CRM
 *   próprio, não por um SDK de terceiro.
 * - **GetBlue** (`event.getblue.io/js/blue-tag.min.js`) — removido em
 *   06/10/2026: a loja não tem conta ativa, e o mesmo script também era
 *   injetado por 4 tags do GTM (removidas lá no mesmo dia).
 * - `gtag.js` carregado direto (`G-XV2RE30HNF`) — o container do GTM abaixo
 *   quase certamente já gerencia o GA4 como tag própria (o `clarity.ms` do
 *   v1 já carrega com `?ref=gtm`, confirmando que pelo menos parte do stack
 *   é gerido de dentro do GTM); carregar os dois dispararia pageview em
 *   dobro no GA4. Confirmar no painel do Tag Manager (conta que gerencia
 *   GTM-N7CX5NT) se o GA4 já está lá dentro antes de decidir se este
 *   `gtag.js` direto precisa voltar.
 *
 * O restante (Clarity, Meta Pixel, Pinterest, Bing Ads, Google Ads
 * conversion) são tags configuradas dentro do próprio GTM — voltam a
 * disparar sozinhas assim que o container abaixo carrega, sem precisar de
 * código aqui.
 *
 * **Carregamento adiado (achado 4 da auditoria de SEO, 2026-08-06):** o GTM
 * sozinho soma ~200 KB de JS (a maior parte não usada no primeiro
 * carregamento) e, junto das tags que ele dispara (GA4, Meta Pixel,
 * Pinterest, Clarity), contribuía ~1s de trabalho de main-thread durante a
 * hidratação (medido via Lighthouse `mainthread-work-breakdown` contra o
 * deploy real). O snippet do GTM abaixo (conteúdo idêntico ao de antes)
 * só dispara na primeira interação real do usuário
 * (`scroll`/`mousemove`/`touchstart`/`keydown`/`click`), ou via
 * `requestIdleCallback` assim que a thread principal ficar livre, ou depois
 * de 4s — o que vier primeiro. Seguro pra conversão: todo evento que
 * importa (`AddToCart`, `select_item`, checkout) exige um clique antes de
 * disparar, e clique já é o próprio gatilho de carregamento. `sendEvent()`
 * (`~/sdk/analytics.tsx`) já faz `window.dataLayer = window.dataLayer || []`
 * antes de dar `push`, então eventos disparados antes da interação (ex:
 * `view_item_list` no load da página) continuam sendo enfileirados
 * normalmente — é o contrato oficial de instalação do GTM, que processa a
 * fila assim que carrega. Ver
 * `docs/superpowers/specs/2026-08-06-defer-third-party-scripts-design.md`.
 */
const THIRD_PARTY_SCRIPTS = [
  {
    id: "deferred-third-party",
    children: `(function () {
  var loaded = false;
  // Visitante que recusou o banner de cookies (opt-out, ver ~/utils/consent):
  // nada de terceiros. Storage ilegível conta como recusa, igual ao
  // hasCookieConsent(). Roda de novo a cada visita, então vale a partir da
  // próxima página carregada (o que já carregou nesta aba não dá pra descarregar).
  function consented() {
    try {
      return localStorage.getItem("store-cookie-consent") !== "declined";
    } catch (e) {
      return false;
    }
  }
  // external_id da Meta desde o 1º hit da visita: cria o dt_visitor_id (mesmo
  // formato de ~/utils/visitorId) e grava o cache dt_meta_user_data (mesmo
  // formato de ~/sdk/metaUserData) ANTES do GTM, que o lê no fbq("init").
  // Sem isso, a 1ª página saía sem external_id no Pixel e no beacon do
  // servidor (diagnóstico "s2s_missing_pii_or_external_id", 21/10/2026).
  // syncMetaUserData() completa depois (e-mail, login).
  var userDataReady = null;
  try {
    if (consented() && !localStorage.getItem("dt_meta_user_data") && window.crypto && crypto.subtle && crypto.randomUUID) {
      var m = document.cookie.match(/(?:^|;\\s*)dt_visitor_id=([^;]*)/);
      var vid = m ? decodeURIComponent(m[1]).trim() : "";
      if (!vid) {
        vid = crypto.randomUUID();
        document.cookie = "dt_visitor_id=" + encodeURIComponent(vid) + "; Max-Age=31536000; Path=/; SameSite=Lax";
      }
      userDataReady = crypto.subtle.digest("SHA-256", new TextEncoder().encode(vid)).then(function (buf) {
        var hex = Array.prototype.map.call(new Uint8Array(buf), function (b) {
          return ("0" + b.toString(16)).slice(-2);
        }).join("");
        if (!localStorage.getItem("dt_meta_user_data")) {
          localStorage.setItem("dt_meta_user_data", JSON.stringify({ external_id: hex }));
        }
      });
    }
  } catch (e) {
    userDataReady = null;
  }
  function load() {
    if (loaded) return;
    if (!consented()) return;
    loaded = true;
    if (userDataReady) {
      // Só alguns ms; se falhar, o GTM carrega igual.
      userDataReady.then(insertGtm, insertGtm);
    } else {
      insertGtm();
    }
  }
  function insertGtm() {
    (function (w, d, s, l, i) {
      w[l] = w[l] || [];
      w[l].push({ "gtm.start": new Date().getTime(), event: "gtm.js" });
      var f = d.getElementsByTagName(s)[0],
        j = d.createElement(s),
        dl = l != "dataLayer" ? "&l=" + l : "";
      j.async = true;
      // Google tag gateway (src/server/tagGateway.ts): mesmo gtm.js, servido
      // pelo próprio domínio.
      j.src = "/metrics/?id=" + i + dl;
      f.parentNode.insertBefore(j, f);
    })(window, document, "script", "dataLayer", "GTM-N7CX5NT");
  }
  ["scroll", "mousemove", "touchstart", "keydown", "click"].forEach(function (evt) {
    window.addEventListener(evt, load, { once: true, passive: true });
  });
  if ("requestIdleCallback" in window) {
    requestIdleCallback(load, { timeout: 4000 });
  } else {
    setTimeout(load, 4000);
  }
})();`,
  },
];

function RootLayout() {
  useAttributionSync();
  useEffect(() => {
    // Efeito de ROOT: uma exceção aqui não quebraria só o tracking, quebraria
    // a hidratação/render do app inteiro. `installNavTrackingHook` e
    // `flushBuffer` já são internamente à prova de throw (ver Finding 2 da
    // revisão final), mas o try/catch fica como rede de segurança final --
    // nenhuma linha de tracking pode derrubar a loja.
    try {
      // Antes do hook: o backfill da fila já manda o view_item da carga pro
      // servidor (~/sdk/metaCapi), que lê o external_id deste cookie.
      getOrCreateVisitorId();
      installNavTrackingHook();
      // Drena o que sobrou de uma sessão anterior (reload no meio da
      // navegação) antes de agendar o próximo ciclo.
      flushBuffer();
      // Correspondência avançada da Meta pro cliente logado. Assíncrono e
      // à prova de throw (ver ~/sdk/metaUserData).
      void syncMetaUserData();
      return scheduleNavTrackingFlush();
    } catch (error) {
      console.error("[navTracking] falha ao inicializar tracking de navegação:", error);
      return undefined;
    }
  }, []);

  return (
    <DecoRootLayout
      lang="pt-BR"
      siteName="dt-tanstack"
      account="dt"
      bodyClassName="bg-base-100 text-base-content"
    >
      {/* Aviso de cookies global: o tracking é opt-out (ver ~/utils/consent),
          então o banner precisa aparecer em todas as páginas, sem depender de
          seção no CMS. Textos vêm dos padrões do componente. */}
      <CookieConsent />
    </DecoRootLayout>
  );
}
